import BigNumber from 'bignumber.js';
// ref: https://hackernoon.com/reducing-the-pain-of-big-numbers-and-introducing-w3bnumber
/**
 * arithmetic operations on big numbers: use bignumber.js, especially division
 * contract interaction: bigint
 * kmb short form: Intl.NumberFormat {"minimumFractionDigits": 1, "maximumFractionDigits": 3}
 */

/**
 * readable number to big number for contract interaction
 * use case: contract interaction
 */
export const toBigValue = (
  value: string | BigNumber,
  decimals?: number,
): bigint => {
  const bignum = BigNumber.isBigNumber(value) ? value : new BigNumber(value);

  //  1.1 Convert the value to big value
  const bigValue = bignum.times(new BigNumber(10).pow(decimals ?? 0));
  //  1.2 round and remove the decimal places
  const strippedDecimals = bigValue.toFixed(0);

  //  1.3 return the value in wei
  return BigInt(strippedDecimals);
};

/**
 * bigint to readable representation like 3.1415926 with as much precision as possible
 * use case: user input, precise calculation without handling basis points
 *
 * decimals default to be 0, keep pass in value decimals
 */
export const toReadableValue = (value: bigint, decimals = 0): BigNumber => {
  const bignum = new BigNumber(value.toString());
  const readableNumber = bignum.div(new BigNumber(10).pow(decimals));

  return readableNumber;
};

export type FormatBigValueOptions = FormatNumberOptions & {
  decimals: number;
  roundingMode?: BigNumber.RoundingMode;
};

/**
 * format big number to formatted display text
 * accept: bigint with decimal offsets e.g. `1000000000000000n`
 * use case: ui display
 */

export const formatBigValue = (
  value: bigint,
  opts: FormatBigValueOptions,
): string => {
  const { decimals, roundingMode, ...formatNumberOpts } = opts;
  if (decimals < 0) {
    throw new Error('Decimals must be greater than 0');
  }
  let readableValue = toReadableValue(value, decimals);

  if (
    roundingMode !== undefined &&
    formatNumberOpts.displayDecimals !== undefined
  ) {
    readableValue = readableValue.dp(
      formatNumberOpts.displayDecimals,
      roundingMode,
    );
  }

  return formatNumber(readableValue, formatNumberOpts);
};

export type FormatNumberOptions = {
  displayDecimals?: number;
  kmb?: boolean;
  isTrimTrailingZero?: boolean;
  isUseSeparator?: boolean;
  isShowSign?: boolean;
  prefix?: string;
  suffix?: string;
  saveDecimalsMode?: boolean;
  smallValueMode?: 'show-less-than' | 'zero';
  autoKmb?: boolean;
  /**
   * rounding applied at `displayDecimals` precision before formatting. Defaults
   * to half-up (toLocaleString's behaviour). Use BigNumber.ROUND_DOWN for caps
   * like "Max" so the displayed value never exceeds the real one
   * (e.g. 99.9999 -> 99.99 instead of 100.00).
   */
  roundingMode?: BigNumber.RoundingMode;
} & (
  | {
      smallValueMode?: 'zero';
      handleSmallNumber?: undefined;
    }
  | {
      smallValueMode?: 'show-less-than';
      handleSmallNumber?: (opts: HandleSmallNumberOptions) => string;
    }
);

// format integer part with thousand separators while preserving decimals as typed
export const formatWithThousandSeparators = (
  raw: string | BigNumber | number,
): string => {
  raw = raw.toString();
  if (!raw) return raw;
  const [integerPart, decimalPart] = raw.split('.');
  const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
return decimalPart !== undefined
    ? `${groupedInteger}.${decimalPart}`
    : groupedInteger;
};

/**
 * format 'normal' number to display text
 * accept: readable numbers, number within safe range e.g. `BigNumber(3.1415926)`
 * use case: ui display
 */
export const formatNumber = (
  value: BigNumber | number,
  opts?: FormatNumberOptions,
): string => {
  const {
    displayDecimals = 2,
    kmb,
    isTrimTrailingZero,
    isUseSeparator = true,
    smallValueMode = 'show-less-than',
    isShowSign,
    prefix = '',
    suffix = '',
    saveDecimalsMode = false, // ex. 1.0000000000000 -> 1.00
    autoKmb = true,
    roundingMode,
    handleSmallNumber = handleSmallNumberDefault,
  } = opts || {};
  let returnValue = '';
  let numberVal: number;
  if (typeof value === 'number') {
    numberVal = value;
  } else {
    numberVal = value.toNumber();
  }

  // round at the display precision up-front so toLocaleString (which always
  // rounds half-up) can't nudge the value past the intended rounding direction
  if (roundingMode !== undefined) {
    numberVal = new BigNumber(numberVal)
      .dp(displayDecimals, roundingMode)
      .toNumber();
  }

  let kmbObj = {};
  if (autoKmb) {
    if (numberVal >= 10 ** 6 || numberVal <= -(10 ** 6)) {
      // autoKmb = true, numberVal >= 10 ** 6, will display in kmb format
      kmbObj = {
        notation: 'compact',
        compactDisplay: 'short',
      };
    }
  }
  if (kmb) {
    // kmb to true, always kmb format
    kmbObj = {
      ...kmbObj,
      notation: 'compact',
      compactDisplay: 'short',
    };
  }

  returnValue = numberVal.toLocaleString('en', {
    ...kmbObj,

    minimumFractionDigits: displayDecimals,
    maximumFractionDigits: displayDecimals,

    /**
     * on: shows +/-
     * off: shows - for negative
     */
    signDisplay: isShowSign ? 'always' : 'auto',
    useGrouping: isUseSeparator,
  });

  if (isTrimTrailingZero) {
    returnValue = trimTrailingZeros(returnValue);
  }

  const sign = ['+', '-'].includes(returnValue[0]) ? returnValue[0] : '';
  if (prefix) {
    // combine sign with prefix
    if (sign) {
      returnValue = returnValue.replace(sign, `${sign}${prefix}`);
    } else {
      returnValue = `${prefix}${returnValue}`;
    }
  }
  if (suffix) {
    returnValue = `${returnValue}${suffix}`;
  }
  if (!isUseSeparator) {
    returnValue = returnValue.replaceAll(',', '');
  }

  if (
    smallValueMode === 'show-less-than' &&
    isZero(returnValue) &&
    !isZero(numberVal.toString())
  ) {
    returnValue = handleSmallNumber({
      prefix,
      sign,
      smallNumber: getSmallestNonZeroNumericString(displayDecimals),
      suffix,
    });
  }

  const [integerPart, decimalPart] = returnValue.split('.');

  if (saveDecimalsMode && decimalPart) {
    // ex. 1.0000000000000 -> 1.00
    const adjustedDecimalPart = kmb ? decimalPart.slice(0, -1) : decimalPart;

    if (adjustedDecimalPart.endsWith('0')) {
      const lastNonZeroIndex = adjustedDecimalPart
        .split('')
        .reverse()
        .findIndex((char: string) => char !== '0');
      const indexToSlice =
        lastNonZeroIndex !== -1
          ? adjustedDecimalPart.length - lastNonZeroIndex
          : 2;

      return `${integerPart}.${adjustedDecimalPart.slice(0, indexToSlice)}${
        kmb && decimalPart.slice(-1) !== '0' ? decimalPart.slice(-1) : ''
      }`;
    }
  }

  return returnValue;
};

const trimTrailingZeros = (str: string) => {
  return str.indexOf('.') >= 0
    ? str.replace(/0{0,30}$/, '').replace(/\.$/, '')
    : str;
};

function getSmallestNonZeroNumericString(displayDecimal: number) {
  const d = Math.max(0, displayDecimal); // can't be negative

  return d === 0 ? '1' : '0' + '.' + '0'.repeat(d - 1) + '1';
}

function isZero(valueStr: string) {
  return valueStr.match(/[1-9]/) === null;
}

type HandleSmallNumberOptions = {
  /**
   * '+' | '-' | ''
   */
  sign: string;
  /**
   * e.g. $
   */
  prefix: string;
  /**
   */
  suffix: string;
  /**
   * formatted number that is 1 decimal larger than the actual value
   * e.g. 0.01
   */
  smallNumber: string;
};

function handleSmallNumberDefault({
  prefix,
  sign,
  smallNumber,
  suffix,
}: HandleSmallNumberOptions) {
  return `< ${sign}${prefix}${smallNumber}${suffix}`;
}

export const ZERO_BIG_NUMBER = new BigNumber(0);
